import React from "react";
import slackIcon from "../../../../assets/Images/Slack.png";
import microsoftteamIcon from "../../../../assets/Images/MicrosoftTeams.png";

export const CHANNEL_PROVIDER_IDS = ["slack", "microsoft-teams"];

export const DEFAULT_OUTPUT_OPTION = {
  value: "chatUi",
  label: "Chats",
  name: "chatui",
};

export const SCHEDULE_OPTIONS = [
  { value: "none", label: "No repeat" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "weekdays", label: "Week Days Only" },
  { value: "weekends", label: "Weekends Only" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export const WEEK_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

export const normalizeProviderId = (provider) => {
  if (!provider) return "";

  if (typeof provider === "string") {
    return provider.toLowerCase();
  }

  return (
    provider?.provider_id ||
    provider?.provider ||
    provider?.provider_name ||
    provider?.name ||
    provider?.value ||
    ""
  )
    .toString()
    .toLowerCase();
};

export const getProviderName = (provider) =>
  provider?.provider_name ||
  provider?.name ||
  provider?.provider ||
  provider?.provider_id ||
  provider?.value ||
  "";

const FAVICON_DOMAIN_MAP = {
  notion: "notion.so",
  clickup: "clickup.com",
  monday: "monday.com",
  discord: "discord.com",
  linear: "linear.app",
  trello: "trello.com",
  asana: "asana.com",
  jira: "atlassian.com",
  github: "github.com",
  gitlab: "gitlab.com",
  "google-docs": "docs.google.com",
  "google-sheets": "sheets.google.com",
  "google-drive": "drive.google.com",
};

const formatProviderDisplayName = (value) =>
  value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const createProviderSelectOption = (provider) => {
  const providerId = normalizeProviderId(provider);
  const rawName = getProviderName(provider);
  const providerName =
    rawName && rawName !== providerId ? rawName : formatProviderDisplayName(providerId);

  let iconSource = null;
  if (providerId === "slack") iconSource = slackIcon;
  else if (providerId === "microsoft-teams") iconSource = microsoftteamIcon;
  else if (FAVICON_DOMAIN_MAP[providerId]) {
    iconSource = `https://www.google.com/s2/favicons?sz=64&domain_url=${FAVICON_DOMAIN_MAP[providerId]}`;
  }

  return {
    value: providerId,
    label: iconSource ? (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <img
          src={iconSource}
          alt={providerName}
          style={{ width: 16, height: 16 }}
        />
        <span>{providerName}</span>
      </div>
    ) : (
      providerName
    ),
    name: providerName,
  };
};

export const createInitialOutputOption = (output) => {
  if (!output) return DEFAULT_OUTPUT_OPTION;

  if (typeof output === "object" && output?.value) {
    return output;
  }

  const normalizedValue = output.toString();

  if (normalizedValue.toLowerCase() === "chatui") {
    return DEFAULT_OUTPUT_OPTION;
  }

  return createProviderSelectOption({ provider_id: normalizedValue });
};

export const isChannelProvider = (providerId) =>
  CHANNEL_PROVIDER_IDS.includes(normalizeProviderId(providerId));

export const formatToTimeInput = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

export const convertLocalToUTC = (localTimeHHMM, offset) => {
  if (!localTimeHHMM || !offset) return null;

  const [hours, minutes] = localTimeHHMM.split(":").map(Number);
  const match = offset.match(/([+-])(\d{2}):(\d{2})/);

  if (!match || Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }

  const [, sign, offsetHours, offsetMinutes] = match;
  const localMinutes = hours * 60 + minutes;
  const timezoneOffsetMinutes =
    (Number(offsetHours) * 60 + Number(offsetMinutes)) *
    (sign === "+" ? 1 : -1);

  let utcMinutes = localMinutes - timezoneOffsetMinutes;
  utcMinutes = (utcMinutes + 1440) % 1440;

  const utcHours = String(Math.floor(utcMinutes / 60)).padStart(2, "0");
  const utcMins = String(utcMinutes % 60).padStart(2, "0");

  return `${utcHours}:${utcMins}`;
};

export const mergeChannels = (existingChannels = [], newChannels = []) => {
  const seenChannelIds = new Set(
    existingChannels.map((channel) => channel?.channel_id),
  );

  const mergedChannels = [...existingChannels];

  newChannels.forEach((channel) => {
    const channelId = channel?.channel_id;

    if (!channelId || seenChannelIds.has(channelId)) return;

    seenChannelIds.add(channelId);
    mergedChannels.push(channel);
  });

  return mergedChannels;
};

export const parseChannelsPayload = (payload) => {
  const normalizedPayload = payload?.data_type
    ? payload
    : payload?.data?.data_type
      ? payload.data
      : payload;

  const channels = Array.isArray(normalizedPayload?.data)
    ? normalizedPayload.data
    : Array.isArray(normalizedPayload)
      ? normalizedPayload
      : [];

  return {
    channels,
    pagination: normalizedPayload?.pagination || {},
  };
};

export const parseProvidersPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

export const getChannelHelpText = ({ outputName, providerId, agencyId }) => {
  const normalizedName = (outputName || "").toLowerCase();
  const normalizedProviderId = normalizeProviderId(providerId);
  const isMaybeAgency = [
    "6739f1be347a7f5974735cec",
    "68f22c8ddcb06046ab258bc2",
  ].some((value) => agencyId?.includes(value));

  const botName = normalizedName.includes("mida")
    ? "MIDAS Slack"
    : normalizedName.includes("spud")
      ? "SPUD"
      : normalizedProviderId === "microsoft-teams" && isMaybeAgency
        ? "Maybe"
        : "the Bot";

  return `Please add @${botName} to the channels you want tasks to be delivered in.`;
};
